<template>
  <div class="container" @click.self="!loading && close()">
    <div v-loading.center="{ isLoading: !!loading }" class="wrapper">
      <h3 class="title">
        <LanguageIcon class="locale-img" />
        Select Language
      </h3>
      <div
        v-for="(lang, locale) in enabledLanguages"
        :key="locale"
        class="item"
        :class="{ selected: locale === selected }"
        @click.stop="locale !== selected && changeLanguage(locale)"
      >
        <div>
          {{ lang.name }}
          <template v-if="lang.englishName">
            <br />
            <span class="subtitle">({{ lang.englishName }})</span>
          </template>
        </div>
        <div class="locale">
          {{ locale }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import * as Locale from '@/plugins/i18n';
import * as env from '@/common/environment';

import loading from '@/components/loading';
import LanguageIcon from './icons/LangaugeIcon.vue';

interface LanguageSelectorProps {
  /**
   * Function called when the dialog is expected to close.
   */
  onCloseDialog: () => unknown;
}

/**
 * Dropdown that selects a language.
 * NOTE: This component is used as a JSX component in the pleased application, and
 * the global i18n service is not available here, hence, we use the locale service directly
 * to listen for changes and reactivity.
 */
@Component({ directives: { loading }, components: { LanguageIcon } })
export default class LanguageSelector extends Vue {
  readonly LANGUAGES = Locale.LANGUAGES;
  selected: Locale.SupportedLanguages = 'en';

  clearLocaleListener: Function | null = null;

  /**
   * The language currently loading. This is used as string to keep
   * track of which language is currently loading.
   */
  loading = '';

  get enabledLanguages() {
    return env.get('enabledLanguages') || [];
  }

  created() {
    this.selected = Locale.i18n.locale as Locale.SupportedLanguages;
    this.clearLocaleListener = Locale.addListener(({ current }) => {
      this.selected = current;
    });
  }

  beforeDestroy() {
    this.clearLocaleListener?.();
  }

  close() {
    this.$emit('closeDialog');
  }

  async changeLanguage(lang: Locale.SupportedLanguages) {
    this.loading = lang;
    try {
      await Locale.setLanguage(lang);
      this.close();
      // TODO: Handle error
    } finally {
      this.loading = '';
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/base.scss';
$border-radius: 5px;
.container {
  height: 100vh;
  width: 100vw;
  background: rgba(0, 0, 0, 0.23);
  display: flex;
  justify-content: center;
  align-items: center;
}

.wrapper {
  font-size: 1.5rem;
  border-radius: $border-radius;
  position: relative;

  box-shadow:
    0 10px 20px rgba(0, 0, 0, 0.19),
    0 6px 6px rgba(0, 0, 0, 0.23);

  .title {
    box-shadow:
      0 4px 2px -3px rgb(0 0 0 / 19%),
      0 4px 11px -3px rgb(0 0 0 / 23%);
    position: relative;
    padding: 1rem 3rem;
    z-index: 101;
    display: flex;
    align-items: center;
    font-weight: 400;
    color: $language-selector-header-text-color;
    background: $language-selector-header-background;
    border: {
      top-left-radius: $border-radius;
      top-right-radius: $border-radius;
    }
    .locale-img {
      width: 32px;
      height: auto;
      margin-right: 10px;
    }
  }

  .item {
    cursor: pointer;
    text-transform: uppercase;
    letter-spacing: 0.2rem;
    padding: 1rem;
    border-bottom: 1px solid $language-selector-item-divider;
    padding: 2rem 3rem;
    display: flex;
    justify-content: space-between;
    z-index: 100;
    background-color: $language-selector-item-background;
    color: $language-selector-item-text-color;

    &:last-child {
      margin-bottom: 0;
      border: 0;
    }

    &.selected {
      background: $language-selector-item-background--selected;
      color: $language-selector-item-text-color--selected;
      box-shadow:
        0 4px 2px -3px rgb(0 0 0 / 19%) inset,
        0 4px 11px -3px rgb(0 0 0 / 23%) inset;
      .subtitle {
        color: $language-selector-item-subtitle-color--selected;
      }
    }

    .subtitle {
      font-size: 1.2rem;
      color: $language-selector-item-subtitle-color;
      letter-spacing: 0.1rem;
      text-transform: capitalize;
    }

    .locale {
      padding-left: 30px;
      text-align: left;
      display: flex;
      align-items: center;
    }
  }
}
</style>
